import React, { useRef, useState, useEffect, useMemo } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import Select, { MultiValue } from 'react-select';
import {
    fetchCampaignSearchFilters,
    fetchCampaignMetadata,
} from '~/services/campaign';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useClearReportFilterParams,
    useReportFilters,
} from '~/context/ReportFiltersProvider';
import { ALL_AVAILABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS, isDefaultSelection, parseVisibleColumns } from './campaignRequestColumns';
import ResetFiltersButton from './ResetFiltersButton';

export type AccordionItem = {
    id: number;
    active: number | null;
    handleToggle: (index: number) => void;
};

interface StatusOption {
    value: string;
    label: string;
}

interface StatusApiResponse {
    code: string;
    name: string;
}

// Date range preset options
interface DatePreset {
    value: string;
    label: string;
    getRange: () => { start: string; end: string };
}

const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

// Date preset options for react-select
const datePresetOptions: StatusOption[] = [
    { value: '', label: 'Select date range' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
];

const contactAttemptsOptions: StatusOption[] = [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4+', label: '4+' },
];

const getDatePresets = (): DatePreset[] => {
    const today = new Date();

    return [
        {
            value: '',
            label: 'Select date range',
            getRange: () => ({ start: '', end: '' }),
        },
        {
            value: 'last_7_days',
            label: 'Last 7 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 7);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_30_days',
            label: 'Last 30 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 30);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_month',
            label: 'This Month',
            getRange: () => {
                const start = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    1,
                );
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_month',
            label: 'Last Month',
            getRange: () => {
                const start = new Date(
                    today.getFullYear(),
                    today.getMonth() - 1,
                    1,
                );
                const end = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_quarter',
            label: 'This Quarter',
            getRange: () => {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const start = new Date(
                    today.getFullYear(),
                    currentQuarter * 3,
                    1,
                );
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_quarter',
            label: 'Last Quarter',
            getRange: () => {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const lastQuarter = currentQuarter - 1;
                const year =
                    lastQuarter < 0
                        ? today.getFullYear() - 1
                        : today.getFullYear();
                const quarter = lastQuarter < 0 ? 3 : lastQuarter;
                const start = new Date(year, quarter * 3, 1);
                const end = new Date(year, quarter * 3 + 3, 0); // Last day of the quarter
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_year',
            label: 'This Year',
            getRange: () => {
                const start = new Date(today.getFullYear(), 0, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_year',
            label: 'Last Year',
            getRange: () => {
                const start = new Date(today.getFullYear() - 1, 0, 1);
                const end = new Date(today.getFullYear() - 1, 11, 31);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'current_fiscal_year',
            label: 'Current Fiscal Year',
            getRange: () => {
                const fyStartYear =
                    today.getMonth() >= 9
                        ? today.getFullYear()
                        : today.getFullYear() - 1;
                const start = new Date(fyStartYear, 9, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
    ];
};

const EMPTY_FORM_VALUES = {
    name: '',
    created_start_date: '',
    created_end_date: '',
    approve_start_date: '',
    approve_end_date: '',
};

const CampaignRequestAccordionItem: React.FC<AccordionItem> = ({
    active,
    handleToggle,
    id,
}) => {
    const contentEl = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setFilterParams, setActiveAccordion } = useReportFilters();
    const clearReportFilterParams = useClearReportFilterParams();
    const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>(
        [],
    );
    const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
    const [cscOptions, setCscOptions] = useState<StatusOption[]>([]);
    const [selectedCsc, setSelectedCsc] = useState<StatusOption | null>(null);
    const [isLoadingCscs, setIsLoadingCscs] = useState(false);
    const [selectedContactAttempts, setSelectedContactAttempts] =
        useState<StatusOption | null>(null);
    const [selectedCreatedDatePreset, setSelectedCreatedDatePreset] =
        useState('');
    const [selectedApproveDatePreset, setSelectedApproveDatePreset] =
        useState('');
    const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);

    // Column picker: seed from the URL (Back-to-Search round-trip), else defaults.
    const [showColumns, setShowColumns] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
        );
    };

    const datePresets = getDatePresets();

    // Determine the status date filter label and param prefix based on selected status
    const statusDateConfig = useMemo(() => {
        if (selectedStatuses.length === 1) {
            const statusCode = selectedStatuses[0].value.toLowerCase();
            if (statusCode === 'pendfinancial') {
                return {
                    label: 'Pend Financial Need Date',
                    presetLabel: 'Pend Financial Need Date Preset',
                    rangeLabel: 'Pend Financial Need Date Range',
                    startParam: 'pend_financial_need_start_date',
                    endParam: 'pend_financial_need_end_date',
                };
            }
            if (statusCode === 'rejected') {
                return {
                    label: 'Reject Date',
                    presetLabel: 'Reject Date Preset',
                    rangeLabel: 'Reject Date Range',
                    startParam: 'reject_start_date',
                    endParam: 'reject_end_date',
                };
            }
        }
        // Default to Approval Date
        return {
            label: 'Approval Date',
            presetLabel: 'Approve Date Preset',
            rangeLabel: 'Date Approved Range',
            startParam: 'approve_start_date',
            endParam: 'approve_end_date',
        };
    }, [selectedStatuses]);

    useEffect(() => {
        const loadStatuses = async () => {
            setIsLoadingStatuses(true);
            try {
                const filtersData = await fetchCampaignSearchFilters();
                const options = filtersData.campaign_request_statuses.map(
                    (status: StatusApiResponse) => ({
                        value: status.code,
                        label: status.name,
                    }),
                );
                setStatusOptions(options);
            } catch (error) {
                console.error(
                    'Error fetching campaign request statuses:',
                    error,
                );
            } finally {
                setIsLoadingStatuses(false);
            }
        };

        loadStatuses();

        const loadCscs = async () => {
            setIsLoadingCscs(true);
            try {
                const metadata = await fetchCampaignMetadata();
                if (metadata?.patient_services_coordinators) {
                    const options = metadata.patient_services_coordinators.map(
                        (psc: { id: number; full_name: string }) => ({
                            value: String(psc.id),
                            label: psc.full_name,
                        }),
                    );
                    setCscOptions(options);
                }
            } catch (error) {
                console.error('Error fetching CSCs:', error);
            } finally {
                setIsLoadingCscs(false);
            }
        };

        loadCscs();
    }, []);

    // Populate form with URL parameters when component loads
    useEffect(() => {
        if (searchParams.size > 0) {
            // Get status-specific date params (check all variants)
            const statusStartDate =
                searchParams.get('approve_start_date') ||
                searchParams.get('pend_financial_need_start_date') ||
                searchParams.get('reject_start_date') ||
                '';
            const statusEndDate =
                searchParams.get('approve_end_date') ||
                searchParams.get('pend_financial_need_end_date') ||
                searchParams.get('reject_end_date') ||
                '';

            // Populate form values
            setFormValues({
                name: searchParams.get('name') || '',
                created_start_date: searchParams.get('start_date') || '',
                created_end_date: searchParams.get('end_date') || '',
                approve_start_date: statusStartDate,
                approve_end_date: statusEndDate,
            });

            // Populate selected statuses
            const statusParams = searchParams.getAll('status');
            if (statusParams.length > 0 && statusOptions.length > 0) {
                const selectedStatusOpts = statusOptions.filter((status) =>
                    statusParams.includes(status.value),
                );
                setSelectedStatuses(selectedStatusOpts);
            }

            // Populate selected CSC
            const cscParam = searchParams.get('csc');
            if (cscParam && cscOptions.length > 0) {
                const matchedCsc = cscOptions.find(
                    (opt) => opt.value === cscParam,
                );
                if (matchedCsc) {
                    setSelectedCsc(matchedCsc);
                }
            }

            // Populate selected contact attempts
            const contactAttemptsParam = searchParams.get('contact_attempts');
            if (contactAttemptsParam) {
                const matchedOption = contactAttemptsOptions.find(
                    (opt) => opt.value === contactAttemptsParam,
                );
                if (matchedOption) {
                    setSelectedContactAttempts(matchedOption);
                }
            }
        }
    }, [searchParams, statusOptions, cscOptions]);

    const handleStatusChange = (selectedOptions: MultiValue<StatusOption>) => {
        setSelectedStatuses([...selectedOptions]);
    };

    const hasActiveFilters =
        Object.values(formValues).some((value) => value !== '') ||
        selectedStatuses.length > 0 ||
        selectedCsc !== null ||
        selectedContactAttempts !== null;

    const handleResetFilters = () => {
        setFormValues(EMPTY_FORM_VALUES);
        setSelectedStatuses([]);
        setSelectedCsc(null);
        setSelectedContactAttempts(null);
        setSelectedCreatedDatePreset('');
        setSelectedApproveDatePreset('');
        clearReportFilterParams();
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const params = new URLSearchParams();

        // Reports search returns every status. The Campaign Requests working
        // list hides rejected/approved/pend-financial/archived by default, and
        // that default would otherwise silently drop them from this report
        // (HHLTB-2125).
        params.append('all_statuses', 'true');

        // Add name/search term (use React state directly for reliability)
        if (formValues.name?.trim()) {
            params.append('name', formValues.name.trim());
        }

        // Add created date range
        if (formValues.created_start_date) {
            params.append('start_date', formValues.created_start_date);
        }

        if (formValues.created_end_date) {
            params.append('end_date', formValues.created_end_date);
        }

        // Add status-specific date range (dynamically named based on selected status)
        if (formValues.approve_start_date) {
            params.append(
                statusDateConfig.startParam,
                formValues.approve_start_date,
            );
        }

        if (formValues.approve_end_date) {
            params.append(
                statusDateConfig.endParam,
                formValues.approve_end_date,
            );
        }

        // Add selected statuses
        selectedStatuses.forEach((status) => {
            params.append('status', status.value);
        });

        // Add selected CSC
        if (selectedCsc) {
            params.append('csc', selectedCsc.value);
        }

        // Add contact attempts filter
        if (selectedContactAttempts) {
            params.append('contact_attempts', selectedContactAttempts.value);
        }

        // Append column selection only when it differs from the defaults,
        // keeping the common-case URL clean. Single comma-joined param to
        // match how the results page reads it.
        if (!isDefaultSelection(selectedColumns) && selectedColumns.length) {
            params.append('visible_columns', selectedColumns.join(','));
        }

        // Save filters to context and navigate to results
        setFilterParams(params.toString());
        setActiveAccordion(3);
        navigate(`/reports/campaign-requests?${params.toString()}`);
    };

    return (
        <div
            className={`w-full max-w-[932px] rounded-sm border bg-white px-7.5 py-4 shadow-default ${
                active === id ? 'border-primary' : 'border-stroke'
            }`}
        >
            <button
                className={`flex w-full items-center justify-between gap-2 ${
                    active === id ? 'active' : ''
                }`}
                onClick={() => handleToggle(id)}
            >
                <div>
                    <h4 className='text-title-xsm font-bold sm:text-title-md'>
                        Campaign Request
                    </h4>
                </div>
                <div className='flex items-center gap-2'>
                    <FaPlus className={`${active === id ? 'hidden' : ''}`} />
                    <FaMinus
                        className={`${active === id ? 'block' : 'hidden'}`}
                    />
                </div>
            </button>

            <div
                ref={contentEl}
                className={`py-5 duration-200 ease-in-out ${
                    active === id ? 'block' : 'hidden'
                }`}
            >
                <form onSubmit={handleSearch}>
                    <div className='flex flex-wrap gap-[30px] lg:flex-nowrap'>
                        <div className='w-full lg:w-3/5'>
                            <h2 className='text-[#949599] text-[18px/22px]'>
                                FILTERS
                            </h2>

                            <div className='mt-4'>
                                <label
                                    htmlFor='cr-name'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Search by Name
                                </label>
                                <input
                                    type='text'
                                    id='cr-name'
                                    name='name'
                                    placeholder='Contact or Client name...'
                                    value={formValues.name}
                                    onChange={(e) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                />
                            </div>

                            <div className='mt-4'>
                                <label
                                    htmlFor='cr-status'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Status (select multiple)
                                </label>
                                <Select
                                    id='cr-status'
                                    menuPlacement='auto'
                                    options={statusOptions}
                                    isMulti
                                    onChange={handleStatusChange}
                                    value={selectedStatuses}
                                    isLoading={isLoadingStatuses}
                                    placeholder='Select statuses...'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                        multiValue: () =>
                                            'bg-[#F4F4F5] rounded-[4px] px-2 py-1 mr-1 mb-1',
                                        multiValueLabel: () => 'text-[14px]',
                                        multiValueRemove: () =>
                                            'hover:bg-[#F02D3A] hover:text-white rounded-r-[4px] px-1',
                                    }}
                                />
                            </div>

                            <div className='mt-4'>
                                <label
                                    htmlFor='cr-csc'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Client Services Coordinator
                                </label>
                                <Select
                                    id='cr-csc'
                                    menuPlacement='auto'
                                    options={cscOptions}
                                    isClearable
                                    isLoading={isLoadingCscs}
                                    value={selectedCsc}
                                    onChange={(option) => {
                                        setSelectedCsc(
                                            option as StatusOption | null,
                                        );
                                    }}
                                    placeholder='Select coordinator...'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                    }}
                                />
                            </div>

                            <div className='mt-4'>
                                <label
                                    htmlFor='cr-contact-attempts'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Contact Attempts
                                </label>
                                <Select
                                    id='cr-contact-attempts'
                                    menuPlacement='auto'
                                    options={contactAttemptsOptions}
                                    isClearable
                                    value={selectedContactAttempts}
                                    onChange={(option) => {
                                        setSelectedContactAttempts(
                                            option as StatusOption | null,
                                        );
                                    }}
                                    placeholder='Select number of attempts...'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                    }}
                                />
                            </div>

                            {/* Status-specific Date Filter (Approve/Reject/Pend Financial Need) */}
                            <div className='mt-[16px] flex flex-wrap items-end gap-4'>
                                <div className='min-w-[150px] flex-1'>
                                    <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                        {statusDateConfig.presetLabel}
                                    </label>
                                    <Select
                                        menuPlacement='auto'
                                        options={datePresetOptions}
                                        isClearable
                                        value={
                                            datePresetOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    selectedApproveDatePreset,
                                            ) || null
                                        }
                                        onChange={(option) => {
                                            const presetValue =
                                                option?.value || '';
                                            setSelectedApproveDatePreset(
                                                presetValue,
                                            );
                                            const preset = datePresets.find(
                                                (p) => p.value === presetValue,
                                            );
                                            if (preset) {
                                                const range = preset.getRange();
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    approve_start_date:
                                                        range.start,
                                                    approve_end_date: range.end,
                                                }));
                                            }
                                        }}
                                        placeholder='Select date range'
                                        classNames={{
                                            control: () =>
                                                'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                            placeholder: () => 'text-[#949599]',
                                        }}
                                    />
                                </div>
                                <div className='flex-1'>
                                    <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                        {statusDateConfig.rangeLabel}
                                    </label>
                                    <div className='flex items-center gap-2'>
                                        <input
                                            type='date'
                                            name='approve_start_date'
                                            value={
                                                formValues.approve_start_date
                                            }
                                            onChange={(e) => {
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    approve_start_date:
                                                        e.target.value,
                                                }));
                                                setSelectedApproveDatePreset(
                                                    '',
                                                ); // Clear preset when manually editing
                                            }}
                                            className='rounded-[4px] border border-[#949599] p-[10px]'
                                        />
                                        <span className='text-gray-500'>
                                            to
                                        </span>
                                        <input
                                            type='date'
                                            name='approve_end_date'
                                            value={formValues.approve_end_date}
                                            onChange={(e) => {
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    approve_end_date:
                                                        e.target.value,
                                                }));
                                                setSelectedApproveDatePreset(
                                                    '',
                                                ); // Clear preset when manually editing
                                            }}
                                            className='rounded-[4px] border border-[#949599] p-[10px]'
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Created Date Filter */}
                            <div className='mt-[16px] flex flex-wrap items-end gap-4'>
                                <div className='min-w-[150px] flex-1'>
                                    <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                        Created Date Preset
                                    </label>
                                    <Select
                                        menuPlacement='auto'
                                        options={datePresetOptions}
                                        isClearable
                                        value={
                                            datePresetOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    selectedCreatedDatePreset,
                                            ) || null
                                        }
                                        onChange={(option) => {
                                            const presetValue =
                                                option?.value || '';
                                            setSelectedCreatedDatePreset(
                                                presetValue,
                                            );
                                            const preset = datePresets.find(
                                                (p) => p.value === presetValue,
                                            );
                                            if (preset) {
                                                const range = preset.getRange();
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    created_start_date:
                                                        range.start,
                                                    created_end_date: range.end,
                                                }));
                                            }
                                        }}
                                        placeholder='Select date range'
                                        classNames={{
                                            control: () =>
                                                'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                            placeholder: () => 'text-[#949599]',
                                        }}
                                    />
                                </div>
                                <div className='flex-1'>
                                    <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                        Date Created Range
                                    </label>
                                    <div className='flex items-center gap-2'>
                                        <input
                                            type='date'
                                            name='created_start_date'
                                            value={
                                                formValues.created_start_date
                                            }
                                            onChange={(e) => {
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    created_start_date:
                                                        e.target.value,
                                                }));
                                                setSelectedCreatedDatePreset(
                                                    '',
                                                ); // Clear preset when manually editing
                                            }}
                                            className='rounded-[4px] border border-[#949599] p-[10px]'
                                        />
                                        <span className='text-gray-500'>
                                            to
                                        </span>
                                        <input
                                            type='date'
                                            name='created_end_date'
                                            value={formValues.created_end_date}
                                            onChange={(e) => {
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    created_end_date:
                                                        e.target.value,
                                                }));
                                                setSelectedCreatedDatePreset(
                                                    '',
                                                ); // Clear preset when manually editing
                                            }}
                                            className='rounded-[4px] border border-[#949599] p-[10px]'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='w-full lg:w-2/5'>
                            <div className='w-full rounded bg-[#F4F4F5] p-5'>
                                <h2 className='text-[#949599] text-[18px/22px]'>
                                    ABOUT THIS SEARCH
                                </h2>
                                <p className='mt-3 text-sm text-gray-600'>
                                    Search for campaign requests by initial
                                    contact name (the person who started the
                                    request) or client name (the patient),
                                    status, date created, or date approved.
                                </p>
                                <p className='mt-3 text-sm text-gray-600'>
                                    <strong>Status codes:</strong>
                                </p>
                                <ul className='mt-2 list-inside list-disc text-sm text-gray-600'>
                                    <li>
                                        <strong>New</strong> - Newly submitted
                                        requests
                                    </li>
                                    <li>
                                        <strong>Assigned</strong> - Assigned to
                                        a coordinator
                                    </li>
                                    <li>
                                        <strong>Approved</strong> - Request has
                                        been approved
                                    </li>
                                    <li>
                                        <strong>Rejected</strong> - Request was
                                        rejected
                                    </li>
                                    <li>
                                        <strong>PendFinancialNeed</strong> -
                                        Pending financial assessment
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Column Configuration */}
                    <div className='mt-8 border-t border-gray-200 pt-6'>
                        <div className='flex items-center justify-between'>
                            <button
                                type='button'
                                onClick={() => setShowColumns((s) => !s)}
                                className='flex items-center gap-2 text-[12px] font-bold text-[#949599]'
                            >
                                COLUMNS ({selectedColumns.length} selected)
                                {showColumns ? (
                                    <FaMinus size={10} />
                                ) : (
                                    <FaPlus size={10} />
                                )}
                            </button>
                            {!isDefaultSelection(selectedColumns) && (
                                <button
                                    type='button'
                                    onClick={() =>
                                        setSelectedColumns(DEFAULT_VISIBLE_COLUMNS)
                                    }
                                    className='text-[12px] text-gray-500 underline hover:text-gray-700'
                                >
                                    Reset to default
                                </button>
                            )}
                        </div>
                        {showColumns && (
                            <div className='mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                                {ALL_AVAILABLE_COLUMNS.map((column) => (
                                    <label
                                        key={column.key}
                                        className='flex cursor-pointer items-center gap-2 text-sm'
                                    >
                                        <input
                                            type='checkbox'
                                            checked={selectedColumns.includes(
                                                column.key,
                                            )}
                                            onChange={() =>
                                                toggleColumn(column.key)
                                            }
                                            className='rounded border-gray-300 text-primary focus:ring-primary'
                                        />
                                        <span className='text-gray-700'>
                                            {column.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                        <ResetFiltersButton
                            onReset={handleResetFilters}
                            disabled={!hasActiveFilters}
                        />
                        <button
                            type='submit'
                            className='rounded-lg bg-primary px-8 py-3 text-center text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Search Campaign Requests
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CampaignRequestAccordionItem;
